import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { 
  FileText, Shield, AlertTriangle, CheckCircle, 
  Loader2, Download, MapPin, Package, Truck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Excluded items - NOT covered
const EXCLUDED_ITEMS = [
  'Perishables',
  'Jewelry',
  'Electronics',
  'Watches',
  'Precious Stones',
  'General Merchandise (Breakage)',
  'Buy for Resale Items',
  'Fragile Items (Breakage Coverage)'
];

// Restricted ZIP codes with special limitations
const RESTRICTED_ZIPS = ['90014', '10006', '10003', '90013'];

// Commodity types that ARE covered
const COMMODITY_TYPES = [
  { value: 'apparel', label: 'Apparel & Clothing', covered: true },
  { value: 'furniture', label: 'Furniture', covered: true },
  { value: 'machinery', label: 'Machinery & Equipment', covered: true },
  { value: 'automotive', label: 'Automotive Parts', covered: true },
  { value: 'building_materials', label: 'Building Materials', covered: true },
  { value: 'paper_products', label: 'Paper Products', covered: true },
  { value: 'plastics', label: 'Plastics & Rubber', covered: true },
  { value: 'textiles', label: 'Textiles & Fabrics', covered: true },
  { value: 'metals', label: 'Metals & Alloys', covered: true },
  { value: 'chemicals', label: 'Chemicals (Non-Hazmat)', covered: true },
  { value: 'cosmetics', label: 'Cosmetics & Beauty', covered: true },
  { value: 'sporting_goods', label: 'Sporting Goods', covered: true },
  { value: 'toys', label: 'Toys & Games', covered: true },
  { value: 'home_goods', label: 'Home Goods', covered: true },
  { value: 'office_supplies', label: 'Office Supplies', covered: true },
  { value: 'medical_supplies', label: 'Medical Supplies (Non-Rx)', covered: true },
  { value: 'tools', label: 'Tools & Hardware', covered: true },
  { value: 'art', label: 'Art & Collectibles', covered: true, special: true },
  { value: 'antiques', label: 'Antiques', covered: true, special: true },
  { value: 'other', label: 'Other (Specify)', covered: true },
];

const CARRIER_TYPES = [
  { value: 'usps', label: 'USPS' },
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'dhl', label: 'DHL' },
  { value: 'freight', label: 'Freight Carrier' },
  { value: 'ltl', label: 'LTL Carrier' },
  { value: 'ftl', label: 'Full Truckload' },
  { value: 'air_cargo', label: 'Air Cargo' },
  { value: 'ocean', label: 'Ocean Freight' },
  { value: 'rail', label: 'Rail Freight' },
];

const COVERAGE_TYPES = [
  { value: 'sellepack', label: 'SelleSend Standard', rate: 0.02, description: 'Basic transit coverage' },
  { value: 'sellesafe', label: 'SelleSafe', rate: 0.035, description: 'Enhanced protection with tracking' },
  { value: 'sellesure', label: 'SelleSure', rate: 0.05, description: 'Full replacement value coverage' },
  { value: 'selleze', label: 'Selleze', rate: 0.025, description: 'Express claims processing' },
  { value: 'sellecheck', label: 'SelleCheck', rate: 0.015, description: 'Verification & documentation only' },
];

const InsuranceDeclarationPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [declarationId, setDeclarationId] = useState(null);
  const { getAuthHeaders } = useAuth();

  const [formData, setFormData] = useState({
    // Shipment Details
    commodity_type: '',
    commodity_description: '',
    declared_value: '',
    quantity: 1,
    weight: '',
    
    // Coverage
    coverage_type: '',
    coverage_amount: '',
    deductible: '100',
    
    // Destination
    origin_zip: '',
    destination_zip: '',
    
    // Carrier
    carrier_type: '',
    carrier_name: '',
    tracking_number: '',
    
    // Timing
    ship_date: '',
    expected_delivery: '',
    reporting_window: '30', // days
    
    // Resale
    is_resale: false,
    resale_invoice: '',
    
    // Acknowledgments
    accept_terms: false,
    acknowledge_exclusions: false,
    confirm_accuracy: false,
  });

  const [zipWarning, setZipWarning] = useState(null);

  const handleZipChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    if (RESTRICTED_ZIPS.includes(value)) {
      setZipWarning(`ZIP ${value} has special coverage limitations. Additional documentation may be required.`);
    } else {
      setZipWarning(null);
    }
  };

  const calculatePremium = () => {
    const coverage = COVERAGE_TYPES.find(c => c.value === formData.coverage_type);
    const amount = parseFloat(formData.coverage_amount) || 0;
    
    if (!coverage) return 0;
    
    let premium = amount * coverage.rate;
    
    // Add 4-5% transaction fee for resale
    if (formData.is_resale) {
      premium += premium * 0.045;
    }
    
    // Special commodity surcharge
    const commodity = COMMODITY_TYPES.find(c => c.value === formData.commodity_type);
    if (commodity?.special) {
      premium += premium * 0.15;
    }
    
    return premium;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accept_terms || !formData.acknowledge_exclusions || !formData.confirm_accuracy) {
      toast.error('Please accept all required acknowledgments');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/insurance/declaration`, {
        ...formData,
        premium: calculatePremium(),
        declared_value: parseFloat(formData.declared_value),
        coverage_amount: parseFloat(formData.coverage_amount),
        quantity: parseInt(formData.quantity),
        weight: parseFloat(formData.weight),
      }, {
        headers: getAuthHeaders()
      });
      
      setDeclarationId(response.data.id);
      setSubmitted(true);
      toast.success('Insurance declaration submitted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit declaration');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="declaration-success">
        <Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Declaration Submitted</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Your insurance declaration has been recorded.
            </p>
            <code className="block bg-white dark:bg-slate-900 p-3 rounded-lg font-mono text-sm mb-6">
              Declaration ID: {declarationId}
            </code>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => window.print()}>
                <Download className="w-4 h-4 mr-2" />
                Print Declaration
              </Button>
              <Button onClick={() => { setSubmitted(false); setFormData({...formData, accept_terms: false, acknowledge_exclusions: false, confirm_accuracy: false}); }}>
                New Declaration
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="insurance-declaration-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Insurance Declaration</h1>
        <p className="text-slate-500 mt-1">Statement of Coverage for Shipment Insurance</p>
      </div>

      {/* Exclusions Notice */}
      <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/10">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-400 mb-2">Items NOT Covered</h3>
              <div className="grid sm:grid-cols-2 gap-2 text-sm text-amber-700 dark:text-amber-300">
                {EXCLUDED_ITEMS.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Commodity Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              Commodity Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Commodity Type *</Label>
                <Select
                  value={formData.commodity_type}
                  onValueChange={(v) => setFormData({...formData, commodity_type: v})}
                >
                  <SelectTrigger data-testid="commodity-type-select">
                    <SelectValue placeholder="Select commodity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMODITY_TYPES.filter(c => c.covered).map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label} {type.special && '⚠️'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Declared Value ($) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.declared_value}
                  onChange={(e) => setFormData({...formData, declared_value: e.target.value})}
                  placeholder="0.00"
                  required
                  data-testid="declared-value-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Commodity Description *</Label>
              <Textarea
                value={formData.commodity_description}
                onChange={(e) => setFormData({...formData, commodity_description: e.target.value})}
                placeholder="Detailed description of items being shipped..."
                required
                data-testid="commodity-description"
              />
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  data-testid="quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Total Weight (lbs)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  placeholder="0.0"
                  data-testid="weight-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coverage Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              Coverage Selection
            </CardTitle>
            <CardDescription>
              Select your coverage type and amount
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {COVERAGE_TYPES.map((coverage) => (
                <div
                  key={coverage.value}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.coverage_type === coverage.value
                      ? 'border-secondary bg-secondary/5'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
                  }`}
                  onClick={() => setFormData({...formData, coverage_type: coverage.value})}
                  data-testid={`coverage-${coverage.value}`}
                >
                  <p className="font-medium">{coverage.label}</p>
                  <p className="text-sm text-slate-500">{coverage.description}</p>
                  <p className="text-lg font-semibold mt-2 text-secondary">{(coverage.rate * 100).toFixed(1)}%</p>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Coverage Amount ($) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.coverage_amount}
                  onChange={(e) => setFormData({...formData, coverage_amount: e.target.value})}
                  placeholder="Amount to insure"
                  required
                  data-testid="coverage-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Deductible ($)</Label>
                <Select
                  value={formData.deductible}
                  onValueChange={(v) => setFormData({...formData, deductible: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">$0</SelectItem>
                    <SelectItem value="50">$50</SelectItem>
                    <SelectItem value="100">$100</SelectItem>
                    <SelectItem value="250">$250</SelectItem>
                    <SelectItem value="500">$500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Destination */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-500" />
              Shipping Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Origin ZIP Code *</Label>
                <Input
                  value={formData.origin_zip}
                  onChange={(e) => handleZipChange('origin_zip', e.target.value)}
                  placeholder="00000"
                  maxLength={5}
                  required
                  data-testid="origin-zip-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Destination ZIP Code *</Label>
                <Input
                  value={formData.destination_zip}
                  onChange={(e) => handleZipChange('destination_zip', e.target.value)}
                  placeholder="00000"
                  maxLength={5}
                  required
                  data-testid="destination-zip-input"
                />
              </div>
            </div>
            
            {zipWarning && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                {zipWarning}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Carrier Type *</Label>
                <Select
                  value={formData.carrier_type}
                  onValueChange={(v) => setFormData({...formData, carrier_type: v})}
                >
                  <SelectTrigger data-testid="carrier-type-select">
                    <SelectValue placeholder="Select carrier type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CARRIER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Carrier Name</Label>
                <Input
                  value={formData.carrier_name}
                  onChange={(e) => setFormData({...formData, carrier_name: e.target.value})}
                  placeholder="e.g., UPS, FedEx Ground"
                  data-testid="carrier-name-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Ship Date</Label>
                <Input
                  type="date"
                  value={formData.ship_date}
                  onChange={(e) => setFormData({...formData, ship_date: e.target.value})}
                  data-testid="ship-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={formData.expected_delivery}
                  onChange={(e) => setFormData({...formData, expected_delivery: e.target.value})}
                  data-testid="expected-delivery-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Reporting Window (Days)</Label>
                <Select
                  value={formData.reporting_window}
                  onValueChange={(v) => setFormData({...formData, reporting_window: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 Days</SelectItem>
                    <SelectItem value="14">14 Days</SelectItem>
                    <SelectItem value="30">30 Days</SelectItem>
                    <SelectItem value="60">60 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resale Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              Resale Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Checkbox
                id="is_resale"
                checked={formData.is_resale}
                onCheckedChange={(checked) => setFormData({...formData, is_resale: checked})}
              />
              <Label htmlFor="is_resale" className="cursor-pointer">
                This shipment is for resale purposes (4.5% transaction fee applies)
              </Label>
            </div>
            
            {formData.is_resale && (
              <div className="space-y-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <Label>Invoice/Reference Number</Label>
                <Input
                  value={formData.resale_invoice}
                  onChange={(e) => setFormData({...formData, resale_invoice: e.target.value})}
                  placeholder="Invoice or PO number"
                  data-testid="resale-invoice-input"
                />
                <p className="text-xs text-slate-500">
                  For resale shipments, we provide invoice, get aid, and send on our dime.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Premium Summary */}
        <Card className="bg-slate-50 dark:bg-slate-900">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-medium">Estimated Premium</span>
              <span className="text-3xl font-semibold tabular-nums text-secondary">
                ${calculatePremium().toFixed(2)}
              </span>
            </div>
            <div className="text-sm text-slate-500 space-y-1">
              <div className="flex justify-between">
                <span>Coverage Amount:</span>
                <span>${parseFloat(formData.coverage_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Base Rate:</span>
                <span>{((COVERAGE_TYPES.find(c => c.value === formData.coverage_type)?.rate || 0) * 100).toFixed(1)}%</span>
              </div>
              {formData.is_resale && (
                <div className="flex justify-between text-orange-600">
                  <span>Resale Transaction Fee:</span>
                  <span>+4.5%</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Terms & Acknowledgments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Terms & Acknowledgments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept_terms"
                checked={formData.accept_terms}
                onCheckedChange={(checked) => setFormData({...formData, accept_terms: checked})}
              />
              <Label htmlFor="accept_terms" className="cursor-pointer text-sm leading-relaxed">
                I accept the terms and conditions for SelleSend, SelleSafe, SelleSure, Selleze, and SelleCheck 
                coverage while in the care, custody, and control of SelleSend systems and/or based on acceptance 
                by the carrier and release of the carrier.
              </Label>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="acknowledge_exclusions"
                checked={formData.acknowledge_exclusions}
                onCheckedChange={(checked) => setFormData({...formData, acknowledge_exclusions: checked})}
              />
              <Label htmlFor="acknowledge_exclusions" className="cursor-pointer text-sm leading-relaxed">
                {/* Antes decia que la joyeria, los relojes, las piedras y la electronica NO se
                    cubren: lo contrario de las reglas de Mike del 4-oct (backend/mercaderia.py). */}
                I acknowledge that cash and cash equivalents, vehicles, perishables and food, live animals or
                plants, hazardous materials and weapons are never covered; that fragile items, art, antiques and
                some other goods need an approval code from the office; and that coverage limits apply by value,
                carrier, service, destination and ZIP code.
              </Label>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="confirm_accuracy"
                checked={formData.confirm_accuracy}
                onCheckedChange={(checked) => setFormData({...formData, confirm_accuracy: checked})}
              />
              <Label htmlFor="confirm_accuracy" className="cursor-pointer text-sm leading-relaxed">
                I confirm that all information provided is accurate and that this declaration is based on the 
                illustrative publication of the items or services in connection with this shipment.
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            size="lg" 
            className="btn-press"
            disabled={loading || !formData.accept_terms || !formData.acknowledge_exclusions || !formData.confirm_accuracy}
            data-testid="submit-declaration-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Submit Declaration
                <FileText className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InsuranceDeclarationPage;
